/* =============================================================
   SKYFALL — Globe v4 (2026-10-08): three styles on one engine, chosen by
   data-sk-globe-style on the mount: 'matrix' (default) | 'holo' | 'radar'.
   Dotted-land earth on a dark body with a fresnel atmosphere, faint
   graticule, HQ pulses, animated network arcs HQ -> agents, abstract
   satellite. HTML callouts beside each pin (outward, limb fade, no overlap).
   Auto-rotate + drag. Respects prefers-reduced-motion. Loads as ESM.
   ============================================================= */
(async function () {
  const mount = document.querySelector('[data-sk-globe]');
  if (!mount) return;

  // Defer heavy work until the page has loaded and the main thread is idle.
  if (document.readyState !== 'complete') {
    await new Promise((r) => window.addEventListener('load', r, { once: true }));
  }
  await new Promise((r) => (
    'requestIdleCallback' in window ? requestIdleCallback(r, { timeout: 2500 }) : setTimeout(r, 400)
  ));

  const THREE = await import('three');
  const topojson = await import('topojson-client');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- Styles ------------------------------------------------------
  const STYLES = {
    matrix: { accent: 0x92bcdd, ice: 0xbad6ef, deep: 0x6790b7, land: 0xcfe2f3, agent: 0xc9ccd1,
              base: 0x0a1017, rim: 0x24415f, key: [0.07, 0.11, 0.16], atmoI: 1.5, grat: 0.06, coast: 0.22,
              step: 1.25, dotSize: 1.7, dotOpacity: 0.9, mode: 0, additive: false, arcs: 0.32, spikes: false, scanlines: 0 },
    holo:   { accent: 0x5fd0ff, ice: 0xc4f1ff, deep: 0x2f8fc4, land: 0x8fe3ff, agent: 0x9fe8ff,
              base: 0x02080d, rim: 0x0f5873, key: [0.0, 0.05, 0.08], atmoI: 2.4, grat: 0.11, coast: 0.45,
              step: 1.0, dotSize: 1.5, dotOpacity: 0.85, mode: 1, additive: true, arcs: 0.45, spikes: false, scanlines: 0.05 },
    radar:  { accent: 0xdfe7ee, ice: 0xffffff, deep: 0x8a96a3, land: 0xe8eef4, agent: 0xc9ccd1,
              base: 0x08090b, rim: 0x23272d, key: [0.05, 0.055, 0.06], atmoI: 0.7, grat: 0.05, coast: 0.12,
              step: 1.4, dotSize: 1.7, dotOpacity: 1.0, mode: 2, additive: false, arcs: 0.2, spikes: true, scanlines: 0 },
  };
  const ST = STYLES[mount.dataset.skGlobeStyle] || STYLES.matrix;
  const C_ACCENT = ST.accent, C_ICE = ST.ice, C_DEEP = ST.deep, C_LAND = ST.land, C_AGENT = ST.agent;

  // --- Scene / renderer -----------------------------------------
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  camera.position.set(0, 0, 5);
  camera.lookAt(0, 0.83, 0);

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch (e) {
    mount.classList.add('sk-globe--fallback');
    return;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(0x000000, 0);
  mount.appendChild(renderer.domElement);

  function resize() {
    const w = mount.clientWidth, h = mount.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(mount);
  resize();

  const GLOBE_R = 1.35;
  function latLonToVec3(lat, lon, r) {
    const phi = (90 - lat) * Math.PI / 180;
    const theta = (lon + 180) * Math.PI / 180;
    return new THREE.Vector3(
      -r * Math.sin(phi) * Math.cos(theta),
       r * Math.cos(phi),
       r * Math.sin(phi) * Math.sin(theta),
    );
  }

  const group = new THREE.Group();
  // The live globe has always sat at this offset (a side effect in the old code);
  // keep it so the framing inside the panel is unchanged.
  group.position.copy(latLonToVec3(26.7056, -80.0364, GLOBE_R));
  scene.add(group);

  // --- Body: dark sphere with a soft fresnel rim (also hides the far side) --
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(GLOBE_R, 64, 64),
    new THREE.ShaderMaterial({
      uniforms: { uBase: { value: new THREE.Color(ST.base) }, uRim: { value: new THREE.Color(ST.rim) }, uKey: { value: new THREE.Vector3(...ST.key) }, uScan: { value: ST.scanlines } },
      vertexShader: `varying vec3 vN; varying vec3 vV;
        void main(){ vec4 mv = modelViewMatrix * vec4(position,1.0); vN = normalize(normalMatrix*normal); vV = normalize(-mv.xyz); gl_Position = projectionMatrix*mv; }`,
      fragmentShader: `uniform vec3 uBase; uniform vec3 uRim; uniform vec3 uKey; uniform float uScan; varying vec3 vN; varying vec3 vV;
        void main(){
          float f = pow(1.0 - max(dot(vN, vV), 0.0), 2.6);
          float key = max(dot(vN, normalize(vec3(-0.55, 0.6, 0.6))), 0.0);
          vec3 c = mix(uBase, uRim, f) + uKey * pow(key, 1.6);
          c += uRim * uScan * step(0.5, fract(gl_FragCoord.y / 3.0));
          gl_FragColor = vec4(c, 1.0);
        }`,
    })
  );
  group.add(body);

  // --- Atmosphere: additive back-face glow just outside the body -------
  group.add(new THREE.Mesh(
    new THREE.SphereGeometry(GLOBE_R * 1.13, 64, 64),
    new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(C_ACCENT) }, uI: { value: ST.atmoI } },
      vertexShader: `varying vec3 vN; varying vec3 vV;
        void main(){ vec4 mv = modelViewMatrix * vec4(position,1.0); vN = normalize(normalMatrix*normal); vV = normalize(-mv.xyz); gl_Position = projectionMatrix*mv; }`,
      fragmentShader: `uniform vec3 uColor; uniform float uI; varying vec3 vN; varying vec3 vV;
        void main(){ float i = pow(max(0.0, 0.62 + dot(vN, vV)), 4.0); gl_FragColor = vec4(uColor, 1.0) * i * uI; }`,
      side: THREE.BackSide, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false,
    })
  ));

  // --- Graticule: faint 15° grid ---------------------------------
  function addLine(points, color, opacity, parent) {
    const l = new THREE.Line(new THREE.BufferGeometry().setFromPoints(points),
      new THREE.LineBasicMaterial({ color, transparent: true, opacity }));
    (parent || group).add(l);
    return l;
  }
  for (let lat = -75; lat <= 75; lat += 15) {
    const pts = [];
    for (let lon = -180; lon <= 180; lon += 3) pts.push(latLonToVec3(lat, lon, GLOBE_R * 1.002));
    addLine(pts, C_ACCENT, lat === 0 ? ST.grat * 2.6 : ST.grat);
  }
  for (let lon = -180; lon < 180; lon += 15) {
    const pts = [];
    for (let lat = -90; lat <= 90; lat += 3) pts.push(latLonToVec3(lat, lon, GLOBE_R * 1.002));
    addLine(pts, C_ACCENT, ST.grat);
  }

  // --- Orbit rings (quiet) ---------------------------------------
  [{ tilt: 0.4, radius: 1.25, opacity: 0.14 }, { tilt: -0.3, radius: 1.4, opacity: 0.08 }, { tilt: 1.3, radius: 1.55, opacity: 0.05 }]
    .forEach((cfg) => {
      const pts = [];
      for (let i = 0; i <= 128; i++) { const a = (i / 128) * Math.PI * 2; pts.push(new THREE.Vector3(Math.cos(a) * GLOBE_R * cfg.radius, 0, Math.sin(a) * GLOBE_R * cfg.radius)); }
      const ring = addLine(pts, C_DEEP, cfg.opacity);
      ring.rotation.x = cfg.tilt; ring.rotation.z = cfg.tilt * 0.3;
    });

  // --- Land: dot matrix + faint coastline (from the same land topojson) --
  function dotTextureUnused() {
    const c = document.createElement('canvas'); c.width = c.height = 64;
    const g = c.getContext('2d');
    const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.55, 'rgba(255,255,255,0.9)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.beginPath(); g.arc(32, 32, 32, 0, Math.PI * 2); g.fill();
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace || t.colorSpace; return t;
  }
  function inRing(lon, lat, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1];
      if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }
  // Rings that cross the 180° line (Russia, Fiji) are unwrapped to continuous longitudes;
  // rings that circle a pole (Antarctica) are closed over the pole.
  function unwrap(ring) {
    const out = []; let off = 0, prev = null, sumY = 0;
    for (const [x, y] of ring) {
      if (prev !== null) { const d = x + off - prev; if (d > 180) off -= 360; else if (d < -180) off += 360; }
      out.push([x + off, y]); prev = x + off; sumY += y;
    }
    if (Math.abs(out[out.length - 1][0] - out[0][0]) > 180) {
      const pole = sumY / ring.length < 0 ? -90 : 90;
      out.push([out[out.length - 1][0], pole], [out[0][0], pole]);
    }
    return out;
  }
  let landMat = null;
  async function loadLand() {
    try {
      const landScript = document.querySelector('script[data-land-url]');
      const landUrl = (landScript && landScript.dataset.landUrl) || 'https://cdn.jsdelivr.net/npm/world-atlas@2/land-110m.json';
      const topology = await (await fetch(landUrl)).json();
      const land = topojson.feature(topology, topology.objects.land);
      const polys = [];
      land.features.forEach((f) => {
        const g = f.geometry;
        const list = g.type === 'Polygon' ? [g.coordinates] : g.type === 'MultiPolygon' ? g.coordinates : [];
        list.forEach((rings) => {
          const rs = rings.map(unwrap);
          let minX = 1e9, maxX = -1e9, minY = 90, maxY = -90;
          rs[0].forEach(([x, y]) => { minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y); });
          polys.push({ raw: rings, rings: rs, minX, maxX, minY, maxY });
        });
      });
      const isLand = (lon, lat) => {
        for (const p of polys) {
          if (lat < p.minY || lat > p.maxY) continue;
          for (const L of [lon, lon + 360, lon - 360]) {
            if (L < p.minX || L > p.maxX) continue;
            if (inRing(L, lat, p.rings[0]) && !p.rings.slice(1).some((h) => inRing(L, lat, h))) return true;
          }
        }
        return false;
      };
      // coastline, very faint, for definition
      const coastMat = new THREE.LineBasicMaterial({ color: C_LAND, transparent: true, opacity: ST.coast });
      polys.forEach((p) => p.raw.forEach((ring) => {
        group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(ring.map(([lon, lat]) => latLonToVec3(lat, lon, GLOBE_R * 1.003))), coastMat));
      }));
      // equal-area dot grid, kept only on land
      const STEP = ST.step, pos = [];
      for (let lat = -84; lat <= 84; lat += STEP) {
        const n = Math.max(1, Math.round((360 / STEP) * Math.cos(lat * Math.PI / 180)));
        for (let k = 0; k < n; k++) {
          const lon = -180 + (k + 0.5) * (360 / n);
          const on = isLand(lon, lat);
          if (on) { const v = latLonToVec3(lat, lon, GLOBE_R * 1.004); pos.push(v.x, v.y, v.z); }
        }
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      landMat = new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color(C_LAND) }, uSize: { value: ST.dotSize }, uOpacity: { value: ST.dotOpacity },
                    uPR: { value: renderer.getPixelRatio() }, uTime: { value: 0 }, uMode: { value: ST.mode }, uScanY: { value: 0 }, uSweep: { value: 0 } },
        vertexShader: `uniform float uSize; uniform float uPR; uniform float uTime; uniform float uMode; uniform float uScanY; uniform float uSweep; varying float vB;
          void main(){
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            gl_Position = projectionMatrix * mv;
            gl_PointSize = uSize * uPR * (4.0 / -mv.z);
            vec3 n = normalize(position); float b = 1.0;
            if (uMode > 0.5 && uMode < 1.5) {          // holo: bright travelling scan band + slight flicker
              b = 0.55 + 1.4 * smoothstep(0.09, 0.0, abs(n.y - uScanY));
              b *= 0.88 + 0.12 * sin(uTime * 30.0 + position.x * 40.0 + position.z * 23.0);
            } else if (uMode > 1.5) {                   // radar: dots light up as the sweep passes, then decay
              float a = atan(position.z, -position.x);
              float d = mod(uSweep - a, 6.2831853);
              b = 0.38 + 1.4 * exp(-d * 1.6);
            }
            vB = b;
          }`,
        fragmentShader: `uniform vec3 uColor; uniform float uOpacity; varying float vB;
          void main(){ vec2 c = gl_PointCoord - 0.5; float r = length(c); if (r > 0.5) discard;
            gl_FragColor = vec4(uColor * min(vB, 1.6), smoothstep(0.5, 0.15, r) * uOpacity * min(vB, 1.0)); }`,
        transparent: true, depthWrite: false, blending: ST.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      });
      group.add(new THREE.Points(geo, landMat));
      if (ST.spikes) {                                   // radar: fine data spikes on a subset of land points
        const seg = [];
        for (let k = 0; k < pos.length; k += 9) {
          const v = new THREE.Vector3(pos[k], pos[k + 1], pos[k + 2]);
          const h = 1.004 + 0.008 + 0.04 * Math.pow(Math.abs(Math.sin(k * 12.9898) * 43758.5453 % 1), 3);
          seg.push(v.x, v.y, v.z, v.x * h / 1.004, v.y * h / 1.004, v.z * h / 1.004);
        }
        const sg = new THREE.BufferGeometry();
        sg.setAttribute('position', new THREE.Float32BufferAttribute(seg, 3));
        group.add(new THREE.LineSegments(sg, new THREE.LineBasicMaterial({ color: C_LAND, transparent: true, opacity: 0.22 })));
      }
    } catch (err) { /* silent: globe still renders without land */ }
  }
  loadLand();

  // --- Tags (HTML callouts beside the pin) ------------------------
  const tagsContainer = document.querySelector('[data-sk-globe-tags]');
  const pinTags = [];
  function addTag({ position, kind, id, label, lift }) {
    if (!tagsContainer) return;
    const anchor = new THREE.Object3D();
    anchor.position.copy(position.clone().multiplyScalar(lift || 1.08));
    group.add(anchor);
    const el = document.createElement('div');
    el.className = 'sk-globe__tag' + (kind === 'hq' ? ' sk-globe__tag--hq' : '');
    el.innerHTML = '<span class="sk-globe__tag-id">' + id + '</span>' + label;
    tagsContainer.appendChild(el);
    pinTags.push({ el, anchor, hq: kind === 'hq', w: 0, h: 0 });
  }

  // --- HQs: bright marker + expanding pulse rings ------------------
  const hqStations = [
    { label: 'Ottawa',          lat: 45.4215, lon: -75.6972 },
    { label: 'West Palm Beach', lat: 26.7056, lon: -80.0364 },
  ];
  const pulses = [];
  const ringGeo = new THREE.RingGeometry(0.9, 1, 48);
  hqStations.forEach((s, si) => {
    const pos = latLonToVec3(s.lat, s.lon, GLOBE_R * 1.006);
    const core = new THREE.Mesh(new THREE.CircleGeometry(0.03, 24), new THREE.MeshBasicMaterial({ color: 0xffffff }));
    core.position.copy(pos); core.lookAt(pos.clone().multiplyScalar(2)); group.add(core);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.05, 0.058, 40),
      new THREE.MeshBasicMaterial({ color: C_ICE, side: THREE.DoubleSide, transparent: true, opacity: 0.9 }));
    ring.position.copy(pos); ring.lookAt(pos.clone().multiplyScalar(2)); group.add(ring);
    for (let k = 0; k < 2; k++) {
      const p = new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ color: C_ACCENT, side: THREE.DoubleSide, transparent: true, opacity: 0, depthWrite: false }));
      p.position.copy(pos); p.lookAt(pos.clone().multiplyScalar(2)); group.add(p);
      pulses.push({ mesh: p, offset: k * 0.5 + si * 0.25 });
    }
    addLine([pos.clone(), pos.clone().multiplyScalar(1.1)], C_ICE, 0.85);
    addTag({ position: pos, kind: 'hq', id: 'HQ', label: s.label, lift: 1.1 });
  });

  // --- Field agents --------------------------------------------
  const operatives = [
    { id: 'AGENT 01', label: 'Los Angeles', lat: 34.0522,  lon: -118.2437 },
    { id: 'AGENT 02', label: 'São Paulo',   lat: -23.5505, lon: -46.6333  },
    { id: 'AGENT 03', label: 'London',      lat: 51.5074,  lon: -0.1278   },
    { id: 'AGENT 04', label: 'Cape Town',   lat: -33.9249, lon: 18.4241   },
    { id: 'AGENT 05', label: 'Tokyo',       lat: 35.6762,  lon: 139.6503  },
    { id: 'AGENT 06', label: 'Sydney',      lat: -33.8688, lon: 151.2093  },
    { id: 'AGENT 07', label: 'McMurdo',     lat: -77.8500, lon: 166.6667  },
  ];
  operatives.forEach((op) => {
    const pos = latLonToVec3(op.lat, op.lon, GLOBE_R * 1.006);
    const dot = new THREE.Mesh(new THREE.CircleGeometry(0.018, 18), new THREE.MeshBasicMaterial({ color: C_AGENT }));
    dot.position.copy(pos); dot.lookAt(pos.clone().multiplyScalar(2)); group.add(dot);
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.032, 0.037, 28),
      new THREE.MeshBasicMaterial({ color: C_AGENT, side: THREE.DoubleSide, transparent: true, opacity: 0.5 }));
    ring.position.copy(pos); ring.lookAt(pos.clone().multiplyScalar(2)); group.add(ring);
    addTag({ position: pos, kind: 'op', id: op.id, label: op.label, lift: 1.04 });
  });

  // --- Network arcs: Ottawa -> each agent, with a travelling pulse --
  const ARC_SEG = 96;
  const arcs = [];
  function arcPoints(a, b) {
    const va = latLonToVec3(a.lat, a.lon, 1).normalize(), vb = latLonToVec3(b.lat, b.lon, 1).normalize();
    const ang = va.angleTo(vb), lift = 0.03 + ang * 0.085, pts = [];
    for (let i = 0; i <= ARC_SEG; i++) {
      const t = i / ARC_SEG;
      const v = new THREE.Vector3().copy(va).multiplyScalar(Math.sin((1 - t) * ang)).addScaledVector(vb, Math.sin(t * ang)).divideScalar(Math.sin(ang));
      pts.push(v.multiplyScalar(GLOBE_R * (1.006 + lift * Math.sin(Math.PI * t))));
    }
    return pts;
  }
  operatives.forEach((op, i) => {
    const pts = arcPoints(hqStations[i % 2 === 0 ? 0 : 1], op);
    addLine(pts, C_ACCENT, ST.arcs);
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    const pulse = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: C_ICE, transparent: true, opacity: 0.95 }));
    group.add(pulse);
    arcs.push({ pulse, speed: 0.22 + (i % 3) * 0.05, phase: i * 0.37 });
  });
  addLine(arcPoints(hqStations[0], hqStations[1]), C_ICE, 0.55);   // OTT <-> WPB link

  // --- Style FX ------------------------------------------------------
  let scanRing = null, sweepLine = null;
  if (ST.mode === 1) {
    const pts = []; for (let i = 0; i <= 128; i++) { const a = (i / 128) * Math.PI * 2; pts.push(new THREE.Vector3(Math.cos(a), 0, Math.sin(a))); }
    scanRing = addLine(pts, C_ICE, 0.75);
  }
  if (ST.mode === 2) {
    const pts = []; for (let lat = -90; lat <= 90; lat += 2) pts.push(latLonToVec3(lat, -180, GLOBE_R * 1.012));  // meridian at local angle 0
    sweepLine = addLine(pts, C_ICE, 0.85);
  }

  // --- Orbiters: a realistic satellite ('model') or a small constellation ('constellation') ---
  const SAT_MODE = mount.dataset.skGlobeSat || 'constellation';
  scene.add(new THREE.AmbientLight(0xffffff, 0.55));
  const sun = new THREE.DirectionalLight(0xffffff, 2.2);
  sun.position.set(-3, 3, 4);
  scene.add(sun);

  function fadeTrail(n, color) {                       // trail that fades out behind the craft (additive, vertex colours)
    const posArr = new Float32Array(n * 3), colArr = new Float32Array(n * 3), c = new THREE.Color(color);
    for (let i = 0; i < n; i++) { const k = Math.pow(1 - i / n, 1.6); colArr[i * 3] = c.r * k; colArr[i * 3 + 1] = c.g * k; colArr[i * 3 + 2] = c.b * k; }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    g.setAttribute('color', new THREE.BufferAttribute(colArr, 3));
    const line = new THREE.Line(g, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false }));
    group.add(line);
    return { line, posArr, hist: [], n };
  }
  function pushTrail(t, p) {
    t.hist.unshift(p.clone()); if (t.hist.length > t.n) t.hist.pop();
    for (let i = 0; i < t.n; i++) { const q = t.hist[i] || p; t.posArr[i * 3] = q.x; t.posArr[i * 3 + 1] = q.y; t.posArr[i * 3 + 2] = q.z; }
    t.line.geometry.attributes.position.needsUpdate = true;
  }

  const orbiters = [];
  if (SAT_MODE === 'model') {
    // Bus (silver MLI), two 3-panel solar wings with cell grid, booms, nadir dish, nav light.
    const sat = new THREE.Group();
    const foil = new THREE.MeshStandardMaterial({ color: 0xdfe5ec, metalness: 0.35, roughness: 0.38 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x55606c, metalness: 0.4, roughness: 0.5 });
    const cells = new THREE.MeshStandardMaterial({ color: 0x1b3a66, metalness: 0.7, roughness: 0.22, emissive: 0x16345e, emissiveIntensity: 0.9, side: THREE.DoubleSide });
    const bus = new THREE.Mesh(new THREE.BoxGeometry(0.034, 0.03, 0.046), foil);
    sat.add(bus);
    const band = new THREE.Mesh(new THREE.BoxGeometry(0.0355, 0.006, 0.0475), dark); band.position.y = 0.006; sat.add(band);
    const PW = 0.03, PH = 0.024, GAPW = 0.003;
    const wings = [];
    [1, -1].forEach((side) => {
      const wing = new THREE.Group(); sat.add(wing); wings.push(wing);
      const boom = new THREE.Mesh(new THREE.CylinderGeometry(0.0012, 0.0012, 0.018, 6), dark);
      boom.rotation.z = Math.PI / 2; boom.position.x = side * (0.017 + 0.009); wing.add(boom);
      for (let k = 0; k < 3; k++) {
        const cx = side * (0.017 + 0.018 + PW / 2 + k * (PW + GAPW));
        const panel = new THREE.Mesh(new THREE.PlaneGeometry(PW, PH), cells);
        panel.rotation.x = -Math.PI / 2; panel.position.x = cx; wing.add(panel);
        const grid = [];
        for (let gx = 1; gx < 4; gx++) { const x = cx - PW / 2 + (gx * PW) / 4; grid.push(x, 0.0004, -PH / 2, x, 0.0004, PH / 2); }
        for (let gz = 1; gz < 3; gz++) { const z = -PH / 2 + (gz * PH) / 3; grid.push(cx - PW / 2, 0.0004, z, cx + PW / 2, 0.0004, z); }
        grid.push(cx - PW / 2, 0.0004, -PH / 2, cx + PW / 2, 0.0004, -PH / 2, cx + PW / 2, 0.0004, -PH / 2, cx + PW / 2, 0.0004, PH / 2,
                  cx + PW / 2, 0.0004, PH / 2, cx - PW / 2, 0.0004, PH / 2, cx - PW / 2, 0.0004, PH / 2, cx - PW / 2, 0.0004, -PH / 2);
        const gg = new THREE.BufferGeometry(); gg.setAttribute('position', new THREE.Float32BufferAttribute(grid, 3));
        wing.add(new THREE.LineSegments(gg, new THREE.LineBasicMaterial({ color: 0xa9cdf0, transparent: true, opacity: 0.7 })));
      }
    });
    // dish on the nadir face (local -Y points at the earth)
    const dish = new THREE.Mesh(new THREE.SphereGeometry(0.016, 20, 10, 0, Math.PI * 2, 0, Math.PI * 0.32),
      new THREE.MeshStandardMaterial({ color: 0xe8ecf0, metalness: 0.3, roughness: 0.4, side: THREE.DoubleSide }));
    dish.rotation.x = Math.PI; dish.position.y = -0.031; sat.add(dish);
    const feed = new THREE.Mesh(new THREE.CylinderGeometry(0.0008, 0.0008, 0.012, 6), dark); feed.position.y = -0.024; sat.add(feed);
    const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.0007, 0.0007, 0.02, 6), dark); mast.position.set(0.008, 0.025, 0.012); sat.add(mast);
    const nav = new THREE.Mesh(new THREE.SphereGeometry(0.0022, 8, 8), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true }));
    nav.position.set(0.008, 0.035, 0.012); sat.add(nav);
    sat.scale.setScalar(1.7);
    group.add(sat);
    orbiters.push({ obj: sat, radius: GLOBE_R * 1.36, euler: new THREE.Euler(0.38, 0, 0.16, 'XYZ'), period: 16, phase: 0.6, trail: fadeTrail(60, C_ICE), nav, wings, face: true });
  } else {
    // three orbital planes, four craft each: small glowing points with short fading trails + faint orbit tracks
    const planes = [{ tilt: 0.95, twist: 0.2, r: 1.22 }, { tilt: -0.55, twist: -0.4, r: 1.3 }, { tilt: 0.2, twist: 1.1, r: 1.42 }];
    planes.forEach((pl, pi) => {
      const e = new THREE.Euler(pl.tilt, 0, pl.twist, 'XYZ');
      const track = [];
      for (let i = 0; i <= 160; i++) { const a = (i / 160) * Math.PI * 2; track.push(new THREE.Vector3(Math.cos(a) * GLOBE_R * pl.r, 0, Math.sin(a) * GLOBE_R * pl.r).applyEuler(e)); }
      addLine(track, C_ACCENT, 0.08);
      for (let k = 0; k < 4; k++) {
        const craft = new THREE.Mesh(new THREE.SphereGeometry(0.011, 10, 10), new THREE.MeshBasicMaterial({ color: 0xffffff }));
        const halo = new THREE.Mesh(new THREE.SphereGeometry(0.026, 12, 12), new THREE.MeshBasicMaterial({ color: C_ACCENT, transparent: true, opacity: 0.25, blending: THREE.AdditiveBlending, depthWrite: false }));
        craft.add(halo); group.add(craft);
        orbiters.push({ obj: craft, radius: GLOBE_R * pl.r, euler: e, period: 11 + pi * 3, phase: (k / 4) * Math.PI * 2 + pi, trail: fadeTrail(26, C_ICE), face: false });
      }
    });
  }

  // --- Tag placement ----------------------------------------------
  const tmpVec = new THREE.Vector3(), tmpDir = new THREE.Vector3(), tmpCam = new THREE.Vector3();
  let measureTick = 0;
  function updateTags() {
    if (!pinTags.length) return;
    const rect = mount.getBoundingClientRect();
    const W = rect.width, H = rect.height;
    if (measureTick++ % 60 === 0) pinTags.forEach((t) => { t.w = t.el.offsetWidth; t.h = t.el.offsetHeight; });
    tmpCam.copy(camera.position).sub(group.position).normalize();
    const center = group.position.clone().project(camera);
    const cx = (center.x * 0.5 + 0.5) * W;
    const items = pinTags.map((t) => {
      t.anchor.getWorldPosition(tmpVec);
      tmpDir.copy(tmpVec).sub(group.position).normalize();
      const p = tmpVec.clone().project(camera);
      return { t, dot: tmpDir.dot(tmpCam), x: (p.x * 0.5 + 0.5) * W, y: (p.y * -0.5 + 0.5) * H };
    });
    items.sort((a, b) => (b.t.hq - a.t.hq) || (b.dot - a.dot));
    const placed = [], GAP = 6, OFF = 10;
    // the fixed corner coordinate boxes count as occupied space
    mount.querySelectorAll('.sk-globe__label').forEach((el) => {
      const b = el.getBoundingClientRect();
      if (b.width) placed.push({ l: b.left - rect.left - 4, r: b.right - rect.left + 4, t: b.top - rect.top - 4, b: b.bottom - rect.top + 4 });
    });
    items.forEach(({ t, dot, x, y }) => {
      const min = t.hq ? 0.18 : 0.32;
      let alpha = Math.max(0, Math.min(1, (dot - min) / 0.22));
      const left = x < cx;
      const above = W < 560;                        // narrow panel (phones): label sits above the pin
      const bx = above ? Math.max(4, Math.min(W - t.w - 4, x - t.w / 2)) : (left ? x - OFF - t.w : x + OFF);
      let by = above ? y - t.h - 12 : y - t.h / 2;
      if (above && by < 2) by = y + 12;              // no room above: drop below the pin
      const box = { l: bx - GAP, r: bx + t.w + GAP, t: by - GAP, b: by + t.h + GAP };
      if (box.l < -GAP || box.r > W + GAP || box.t < -GAP || box.b > H + GAP) alpha = 0;
      if (alpha > 0 && placed.some((q) => box.l < q.r && box.r > q.l && box.t < q.b && box.b > q.t)) alpha = 0;
      if (alpha > 0) placed.push(box);
      t.el.classList.toggle('is-left', left && !above);
      t.el.classList.toggle('is-above', above);
      t.el.classList.toggle('is-visible', alpha > 0);
      t.el.style.left = '0'; t.el.style.top = '0';
      t.el.style.transform = 'translate(' + bx.toFixed(1) + 'px, ' + by.toFixed(1) + 'px)';
      t.el.style.opacity = alpha.toFixed(2);
    });
  }

  // --- Motion -------------------------------------------------------
  const ROT_SPEED = reduceMotion ? 0 : 0.12;
  group.rotation.x = 0.3;    // northern hemisphere tilted toward the viewer
  group.rotation.y = -0.7;   // open with North America just left of centre

  let isDragging = false, lastX = 0, lastY = 0;
  const canvas = renderer.domElement;
  canvas.style.cursor = 'grab';
  canvas.style.touchAction = 'none';
  canvas.addEventListener('pointerdown', (e) => { isDragging = true; lastX = e.clientX; lastY = e.clientY; canvas.style.cursor = 'grabbing'; canvas.setPointerCapture(e.pointerId); });
  canvas.addEventListener('pointermove', (e) => {
    if (!isDragging) return;
    group.rotation.y += (e.clientX - lastX) * 0.006;
    group.rotation.x = Math.max(-Math.PI * 0.42, Math.min(Math.PI * 0.42, group.rotation.x + (e.clientY - lastY) * 0.006));
    lastX = e.clientX; lastY = e.clientY;
  });
  function endDrag(e) { if (!isDragging) return; isDragging = false; canvas.style.cursor = 'grab'; try { canvas.releasePointerCapture(e.pointerId); } catch (_) {} }
  ['pointerup', 'pointercancel', 'pointerleave'].forEach((ev) => canvas.addEventListener(ev, endDrag));

  const APPLIED_SCALE = 0.8625;
  group.scale.setScalar(APPLIED_SCALE);
  let t0 = performance.now(), rafId = null, posterCleared = false, inView = true, pageVisible = !document.hidden;
  function startLoop() { if (rafId !== null || !inView || !pageVisible) return; t0 = performance.now(); rafId = requestAnimationFrame(tick); }
  function stopLoop() { if (rafId === null) return; cancelAnimationFrame(rafId); rafId = null; }

  const PULSE_LEN = 22;
  function tick(now) {
    const dt = Math.min(0.1, (now - t0) / 1000); t0 = now;   // clamp: no jump after a throttled/background tab
    const sec = now / 1000;
    group.rotation.y += ROT_SPEED * dt * (isDragging ? 0.05 : 1);

    // style FX
    const scanY = Math.sin(sec * 0.55) * 0.92;
    const sweep = (sec * 1.1) % (Math.PI * 2);
    if (landMat) { landMat.uniforms.uTime.value = sec; landMat.uniforms.uScanY.value = scanY; landMat.uniforms.uSweep.value = sweep; }
    if (scanRing) { const r = Math.sqrt(1 - scanY * scanY) * GLOBE_R * 1.01; scanRing.scale.set(r, 1, r); scanRing.position.y = scanY * GLOBE_R * 1.01; }
    if (sweepLine) sweepLine.rotation.y = sweep;

    // HQ pulse rings: expand and fade, staggered
    pulses.forEach((p) => {
      const k = reduceMotion ? 0.35 : ((sec * 0.45 + p.offset) % 1);
      p.mesh.scale.setScalar(0.06 + k * 0.16);
      p.mesh.material.opacity = (1 - k) * 0.55;
    });
    // travelling pulse along each arc
    arcs.forEach((a) => {
      const k = reduceMotion ? 0.5 : ((sec * a.speed + a.phase) % 1.4);
      const start = Math.floor(Math.min(1, k) * (ARC_SEG + PULSE_LEN)) - PULSE_LEN;
      a.pulse.geometry.setDrawRange(Math.max(0, start), Math.max(0, Math.min(PULSE_LEN, start + PULSE_LEN, ARC_SEG + 1 - start)));
    });

    // orbiters
    orbiters.forEach((o) => {
      const ang = (reduceMotion ? 0 : sec) * (Math.PI * 2 / o.period) + o.phase;
      const p = new THREE.Vector3(Math.cos(ang) * o.radius, 0, Math.sin(ang) * o.radius).applyEuler(o.euler);
      o.obj.position.copy(p);
      if (o.face) {                                   // nadir (-Y) to the earth, velocity along +Z
        const vel = new THREE.Vector3(-Math.sin(ang), 0, Math.cos(ang)).applyEuler(o.euler);
        const up = p.clone().normalize();             // +Y away from the earth
        const z = vel.clone().sub(up.clone().multiplyScalar(vel.dot(up))).normalize();
        const x = new THREE.Vector3().crossVectors(up, z);
        o.obj.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, up, z));
        o.nav.material.opacity = (sec % 1.6) < 0.12 ? 1 : 0.15;   // strobe
        o.obj.updateMatrixWorld(true);
        const camLocal = o.obj.worldToLocal(camera.position.clone()).normalize();
        const tilt = Math.atan2(camLocal.z, camLocal.y) * 0.85;      // arrays rotate on the boom axis toward the viewer
        o.wings.forEach((w) => { w.rotation.x = tilt; });
      }
      pushTrail(o.trail, p);
    });

    updateTags();
    renderer.render(scene, camera);
    if (!posterCleared) { posterCleared = true; mount.classList.add('sk-globe--live'); }
    rafId = requestAnimationFrame(tick);
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => { inView = entries[0].isIntersecting; if (inView) startLoop(); else stopLoop(); }, { threshold: 0 }).observe(mount);
  }
  document.addEventListener('visibilitychange', () => { pageVisible = !document.hidden; if (pageVisible) startLoop(); else stopLoop(); });
  startLoop();

  // --- Morse blink (S · K · Y loop) -------------------------------
  const morseDot = document.querySelector('[data-sk-globe-morse]');
  if (morseDot && !reduceMotion) {
    const U = 180;
    const seq = [['on',1],['off',1],['on',1],['off',1],['on',1],['off',3],['on',3],['off',1],['on',1],['off',1],['on',3],['off',3],
                 ['on',3],['off',1],['on',1],['off',1],['on',3],['off',1],['on',3],['off',7]];
    (function play() { let t = 0; seq.forEach(([s, u]) => { setTimeout(() => morseDot.classList.toggle('is-on', s === 'on'), t); t += u * U; }); setTimeout(play, t); })();
  }

  // --- Mission clock + telemetry ---------------------------------
  const clockEl = document.querySelector('[data-sk-globe-clock]');
  const signalEl = document.querySelector('[data-sk-globe-signal]');
  const missionStart = performance.now();
  function updateTelemetry() {
    const secs = Math.floor((performance.now() - missionStart) / 1000);
    const h = String(Math.floor(secs / 3600)).padStart(2, '0'), m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0'), s = String(secs % 60).padStart(2, '0');
    if (clockEl) clockEl.textContent = h + ':' + m + ':' + s;
    if (signalEl) signalEl.textContent = 'STRONG · ' + (90 + Math.floor(Math.sin(secs * 0.3) * 5) + Math.floor(Math.random() * 4)) + '%';
  }
  if (clockEl || signalEl) { updateTelemetry(); setInterval(updateTelemetry, 500); }
})();
